import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import type { Conversation, PresenceStatus, UserBrief } from "@/messaging/types";
import { CheckIcon, PlusIcon, SearchIcon, TrashIcon } from "./icons";

export function MembersPanel({
  conversation,
  myId,
  presenceOf,
  onChanged,
  onLeft,
}: {
  conversation: Conversation;
  myId: string | null;
  presenceOf: (u: UserBrief | null | undefined) => PresenceStatus;
  onChanged: () => void;
  onLeft: () => void;
}) {
  const isAdmin = conversation.created_by === myId && !conversation.is_system;
  const [adding, setAdding] = useState(false);
  const [teammates, setTeammates] = useState<UserBrief[] | null>(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!adding) return;
    api<UserBrief[]>("/api/messaging/teammates").then(setTeammates).catch(() => setTeammates([]));
  }, [adding]);

  const memberIds = useMemo(
    () => new Set(conversation.members.map((m) => m.user.id)),
    [conversation.members],
  );
  const candidates = useMemo(() => {
    const list = (teammates ?? []).filter((u) => !memberIds.has(u.id));
    const t = q.trim().toLowerCase();
    return t ? list.filter((u) => (u.full_name || u.email).toLowerCase().includes(t)) : list;
  }, [teammates, memberIds, q]);

  const addPicked = async () => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    if (!ids.length) return;
    setBusy(true);
    try {
      await api(`/api/conversations/${conversation.id}/members`, {
        method: "POST",
        json: { user_ids: ids },
      });
      setAdding(false);
      setPicked({});
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: string) => {
    setBusy(true);
    try {
      await api(`/api/conversations/${conversation.id}/members/${userId}`, {
        method: "DELETE",
      });
      if (userId === myId) onLeft();
      else onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (adding) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="px-2.5 py-2">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
              <SearchIcon size={16} />
            </span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Add teammates"
              className="input w-full !pl-9 !py-2 !rounded-lg"
            />
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          {(candidates ?? []).map((u) => (
            <button
              key={u.id}
              onClick={() => setPicked((p) => ({ ...p, [u.id]: !p[u.id] }))}
              className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition ${
                picked[u.id] ? "bg-accent-500/10" : "hover:bg-white/[0.03]"
              }`}
            >
              <Avatar url={u.avatar_url} name={u.full_name} email={u.email} size="sm" status={presenceOf(u)} />
              <span className="min-w-0 flex-1 truncate text-sm text-slate-200">
                {u.full_name || u.email}
              </span>
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                  picked[u.id] ? "border-accent-500 bg-accent-500 text-white" : "border-white/20 text-transparent"
                }`}
              >
                <CheckIcon size={13} />
              </span>
            </button>
          ))}
        </div>
        <div className="border-t border-white/5 p-2.5 flex gap-2">
          <button className="btn-secondary flex-1 !py-2" onClick={() => setAdding(false)}>
            Cancel
          </button>
          <button className="btn-primary flex-1 !py-2" onClick={addPicked} disabled={busy}>
            Add
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between px-3.5 py-2.5">
        <span className="card-title">{conversation.members.length} members</span>
        {isAdmin && (
          <button className="btn-secondary !py-1 !px-2.5 !text-xs" onClick={() => setAdding(true)}>
            <PlusIcon size={14} /> Add
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        {conversation.members.map((m) => (
          <div key={m.user.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.02]">
            <Avatar
              url={m.user.avatar_url}
              name={m.user.full_name}
              email={m.user.email}
              size="sm"
              status={presenceOf(m.user)}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-slate-200">
                {m.user.full_name || m.user.email}
                {m.user.id === myId && <span className="text-slate-500"> (you)</span>}
              </div>
              <div className="truncate text-[11px] text-slate-500">{m.user.email}</div>
            </div>
            {conversation.created_by === m.user.id && (
              <span className="badge border-accent-500/30 bg-accent-500/10 text-accent-200">Admin</span>
            )}
            {isAdmin && m.user.id !== myId && (
              <button
                onClick={() => remove(m.user.id)}
                className="icon-btn h-8 w-8 hover:!text-rose-300"
                title="Remove"
                disabled={busy}
              >
                <TrashIcon size={15} />
              </button>
            )}
          </div>
        ))}
      </div>
      {conversation.kind === "group" && !conversation.is_system && (
        <div className="border-t border-white/5 p-2.5">
          <button className="btn-danger w-full !py-2" onClick={() => remove(myId!)} disabled={busy}>
            Leave group
          </button>
        </div>
      )}
    </div>
  );
}
